/**
 * CRM (AVI-13): the request shapes the web API and the MCP tools both accept,
 * and the record shapes they both return.
 */
import { z } from 'zod';
import {
  CRM_ACCOUNT_SOURCES, CRM_ACTIVITY_DIRECTIONS, CRM_ACTIVITY_KINDS, CRM_LAWFUL_BASES, CRM_LIFECYCLES, CRM_OPERATOR_TYPES, CRM_PERSONAS,
  DEAL_LOST_REASONS, DEAL_STAGES, OPEN_DEAL_STAGES,
  type CrmAccountSource, type CrmActivityDirection, type CrmActivityKind, type CrmActivitySource, type CrmLawfulBasis, type CrmLifecycle,
  type CrmOperatorType, type CrmPersona, type CrmSegment, type DealLostReason, type DealStage,
} from './enums.js';
import { isoDate } from './schemas.js';
import type { CrmEmailAddress, DomainPatternMap } from './outreach.js';

const text = (max: number) => z.string().trim().max(max).optional().nullable();
const datetime = z.string().datetime({ offset: true });

/**
 * A LinkedIn profile URL in its one stored form, `https://www.linkedin.com/in/<slug>`,
 * however it was pasted (scheme, www., country subdomain, query, trailing slash).
 * Null for anything that is not a /in/ profile URL.
 */
export function normaliseLinkedinUrl(url: string): string | null {
  const m = /^(?:https?:\/\/)?(?:[a-z]{2,3}\.|www\.)?linkedin\.com\/in\/([^/?#\s]+)\/?(?:[?#].*)?$/i.exec(url.trim());
  if (!m) return null;
  let slug = m[1]!;
  try { slug = decodeURIComponent(slug); } catch { /* keep it as written */ }
  return `https://www.linkedin.com/in/${encodeURIComponent(slug.toLowerCase())}`;
}

const linkedinUrl = z.string().trim().max(300).transform((v, ctx) => {
  if (!v) return null;
  const n = normaliseLinkedinUrl(v);
  if (!n) { ctx.addIssue({ code: 'custom', message: 'A LinkedIn URL looks like linkedin.com/in/<name>' }); return z.NEVER; }
  return n;
}).optional().nullable();

/** A bare domain: the account's mail domain when it differs from its website. */
const domain = z.string().trim().toLowerCase().max(253)
  .transform((v) => v.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/[/?#].*$/, ''))
  .refine((v) => v === '' || /^([a-z0-9-]+\.)+[a-z]{2,}$/.test(v), 'Not a domain name')
  .optional().nullable();
/** `?flag=true` / `?flag=false` — z.coerce.boolean would read "false" as true. */
const queryBool = z.enum(['true', 'false']).transform((v) => v === 'true').optional();
const page = { limit: z.coerce.number().int().min(1).max(500).default(100), offset: z.coerce.number().int().min(0).default(0) };

// ---- segments ----------------------------------------------------------------

/** A segment key: lower-case letters, digits, `-` and `_`, as stored on an account. */
export const crmSegmentKey = z.string().trim().toLowerCase()
  .regex(/^[a-z0-9][a-z0-9_-]{0,49}$/, 'A segment key is 1–50 lower-case letters, digits, - or _');

export const crmSegmentSchema = z.object({
  name: z.string().trim().min(1).max(100),
  /** Derived from the name when left out. */
  key: crmSegmentKey.optional(),
  description: text(500),
});
export type CrmSegmentDto = z.input<typeof crmSegmentSchema>;

/** The key stays: accounts refer to it. */
export const updateCrmSegmentSchema = crmSegmentSchema.omit({ key: true }).partial();
export type UpdateCrmSegmentDto = z.input<typeof updateCrmSegmentSchema>;

// ---- accounts ----------------------------------------------------------------

export const crmAccountSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** Required so the duplicate check on (name, country) cannot be dodged by leaving it empty. */
  country: z.string().trim().length(2).transform((c) => c.toUpperCase()),
  website: text(300),
  /** A built-in segment or one the company added. */
  segment: crmSegmentKey.default('other'),
  operatorType: z.enum(CRM_OPERATOR_TYPES).optional().nullable(),
  fleetSize: text(50),
  mainAircraft: text(200),
  aocReference: text(100),
  priority: z.number().int().min(1).max(3).optional().nullable(),
  triggerNote: text(1000),
  smsApproach: text(2000),
  source: z.enum(CRM_ACCOUNT_SOURCES).default('manual'),
  sourceRef: text(200),
  sourceUrl: text(500),
  ownerUserId: z.string().uuid().optional().nullable(),
  notes: text(10_000),
  /** The domain addresses are derived on; defaults to the website's. */
  emailDomain: domain,
});
export type CrmAccountDto = z.input<typeof crmAccountSchema>;

export const updateCrmAccountSchema = crmAccountSchema.partial().extend({
  /**
   * `.partial()` keeps a defaulted field's default applied when the key is
   * omitted, which would silently reset it on every partial update — so the
   * defaulted fields are redeclared here without their create-time default.
   */
  segment: crmSegmentKey.optional(),
  source: z.enum(CRM_ACCOUNT_SOURCES).optional(),
  /** `customer` is reached by converting a won deal, never set directly. */
  lifecycle: z.enum(CRM_LIFECYCLES).exclude(['customer']).optional(),
});
export type UpdateCrmAccountDto = z.input<typeof updateCrmAccountSchema>;

export const crmAccountListQuery = z.object({
  segment: crmSegmentKey.optional(),
  country: z.string().length(2).transform((c) => c.toUpperCase()).optional(),
  priority: z.coerce.number().int().min(1).max(3).optional(),
  lifecycle: z.enum(CRM_LIFECYCLES).optional(),
  ownerUserId: z.string().uuid().optional(),
  hasTrigger: queryBool,
  /** Only accounts that may be approached: leaves out `do_not_contact`. */
  outreach: queryBool,
  q: z.string().max(100).optional(),
  ...page,
});
export type CrmAccountListQuery = z.output<typeof crmAccountListQuery>;

// ---- people --------------------------------------------------------------------

export const crmPersonSchema = z.object({
  accountId: z.string().uuid(),
  name: z.string().trim().min(1).max(200),
  /** Used to derive addresses; split from `name` when left out. */
  firstName: text(100),
  /** Including any prefix: "de Vries". */
  lastName: text(100),
  /** Only when known, e.g. "J.P."; adds the j.p.devries form to the candidates. */
  initials: text(20),
  roleTitle: text(200),
  persona: z.enum(CRM_PERSONAS).default('other'),
  /** The primary address. More addresses, each with a status, go through /people/:id/emails. */
  email: z.string().trim().toLowerCase().email().max(200).optional().nullable(),
  phone: text(50),
  linkedinUrl,
  lawfulBasis: z.enum(CRM_LAWFUL_BASES),
  basisNote: text(1000),
  /** Where the contact details came from. Required: no person without a source (GDPR). */
  source: z.string().trim().min(1).max(300),
});
export type CrmPersonDto = z.input<typeof crmPersonSchema>;

/** Opt-out and contact dates are not editable here: they follow from the opt-out action and from logged activities. */
export const updateCrmPersonSchema = crmPersonSchema.omit({ accountId: true }).partial().extend({
  /** See the comment on `updateCrmAccountSchema`: redeclared without its create-time default so an omitted `persona` leaves it unchanged. */
  persona: z.enum(CRM_PERSONAS).optional(),
});
export type UpdateCrmPersonDto = z.input<typeof updateCrmPersonSchema>;

export const crmOptOutSchema = z.object({
  /** How they told us: "email reply", "phone", "portal", … */
  channel: z.string().trim().min(1).max(100).default('unspecified'),
});

export const crmPersonListQuery = z.object({
  accountId: z.string().uuid().optional(),
  persona: z.enum(CRM_PERSONAS).optional(),
  optedOut: queryBool,
  q: z.string().max(100).optional(),
  /** Any of a person's addresses, matched exactly (through email_keys). */
  email: z.string().trim().toLowerCase().max(254).optional(),
  ...page,
});
export type CrmPersonListQuery = z.output<typeof crmPersonListQuery>;

// ---- deals -----------------------------------------------------------------------

const dealFields = {
  title: z.string().trim().min(1).max(200),
  ownerUserId: z.string().uuid().optional().nullable(),
  primaryPersonId: z.string().uuid().optional().nullable(),
  /** AvioData module ids the deal is about. */
  modules: z.array(z.string().trim().min(1).max(50)).max(30).optional(),
  expectedCloseDate: isoDate.optional().nullable(),
  demoAt: datetime.optional().nullable(),
  proposalUrl: text(500),
};

export const crmDealSchema = z.object({
  accountId: z.string().uuid(),
  ...dealFields,
  /** A deal starts in an open stage; won, paying and lost are reached by moving it. */
  stage: z.enum(OPEN_DEAL_STAGES as [DealStage, ...DealStage[]]).default('qualified'),
});
export type CrmDealDto = z.input<typeof crmDealSchema>;

export const updateCrmDealSchema = z.object(dealFields).partial();
export type UpdateCrmDealDto = z.input<typeof updateCrmDealSchema>;

export const crmStageMoveSchema = z.object({
  /** `paying` is set only by the first paid invoice. */
  stage: z.enum(DEAL_STAGES).exclude(['paying']),
  /** Contract date; required for `won`. */
  wonAt: isoDate.optional(),
  /** Required for `lost`. */
  lostReason: z.enum(DEAL_LOST_REASONS).optional(),
  lostNote: text(1000),
  demoAt: datetime.optional(),
  proposalUrl: z.string().trim().min(1).max(500).optional(),
  /** Written on the activity the move creates. */
  note: text(2000),
});
export type CrmStageMoveDto = z.input<typeof crmStageMoveSchema>;

export const crmDealListQuery = z.object({
  stage: z.enum(DEAL_STAGES).optional(),
  ownerUserId: z.string().uuid().optional(),
  accountId: z.string().uuid().optional(),
  /** Only deals still in an open stage. */
  open: queryBool,
  ...page,
});
export type CrmDealListQuery = z.output<typeof crmDealListQuery>;

// ---- activities -------------------------------------------------------------------

export const crmActivitySchema = z.object({
  accountId: z.string().uuid(),
  dealId: z.string().uuid().optional().nullable(),
  personId: z.string().uuid().optional().nullable(),
  kind: z.enum(CRM_ACTIVITY_KINDS),
  /** Required for call, email and linkedin; not allowed for the other kinds. */
  direction: z.enum(CRM_ACTIVITY_DIRECTIONS).optional().nullable(),
  subject: z.string().trim().min(1).max(300),
  body: text(20_000),
  /** When it happened; defaults to now. */
  occurredAt: datetime.optional(),
  /** Required for a task. */
  dueAt: datetime.optional().nullable(),
});
export type CrmActivityDto = z.input<typeof crmActivitySchema>;

export const updateCrmActivitySchema = z.object({
  subject: z.string().trim().min(1).max(300).optional(),
  body: text(20_000),
  dueAt: datetime.optional().nullable(),
  /** Tasks only: true completes it, false reopens it. */
  done: z.boolean().optional(),
});
export type UpdateCrmActivityDto = z.input<typeof updateCrmActivitySchema>;

export const crmActivityListQuery = z.object({
  accountId: z.string().uuid().optional(),
  dealId: z.string().uuid().optional(),
  personId: z.string().uuid().optional(),
  kind: z.enum(CRM_ACTIVITY_KINDS).optional(),
  /** Open tasks only, overdue first. */
  openTasks: queryBool,
  /** With openTasks: only those created by the caller. */
  mine: queryBool,
  ...page,
});
export type CrmActivityListQuery = z.output<typeof crmActivityListQuery>;

// ---- conversion and funnel ----------------------------------------------------------

export const crmConvertSchema = z.object({
  /** Link this existing customer instead of creating one. */
  customerId: z.string().uuid().optional(),
});

export const crmFunnelQuery = z.object({
  /** Inclusive; omitted means since the first deal. */
  from: isoDate.optional(),
  /** Inclusive; omitted means today. */
  to: isoDate.optional(),
});

/** The board's funnel for 10 paying customers by 7 Apr 2027 (AVI-13 §1). */
export const CRM_FUNNEL_TARGETS = { qualifiedContacts: 400, conversations: 60, demos: 30, proposals: 15, payingCustomers: 10 } as const;
export type CrmFunnelLineKey = keyof typeof CRM_FUNNEL_TARGETS;

const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]);

/** The VAT treatment a new business customer in `country` starts with; a person checks it before the first invoice. */
export function defaultVatTreatmentFor(country: string): 'nl_21' | 'reverse_charge' | 'outside_eu' {
  const c = country.toUpperCase();
  return c === 'NL' ? 'nl_21' : EU_COUNTRIES.has(c) ? 'reverse_charge' : 'outside_eu';
}

// ---- intake (AVI-13 §6) --------------------------------------------------------------

/**
 * The columns of the ICP target lists (AVI-4). Organisations only: the import takes no person columns.
 * `segment` is optional: a segment's key or name; one the company does not have yet is added. Left
 * empty, the segment follows from `type`.
 */
export const CRM_IMPORT_COLUMNS = [
  'operator_name', 'country', 'type', 'fleet_size_approx', 'main_aircraft', 'website', 'sms_approach_public', 'priority_segment', 'source_url', 'notes', 'segment',
] as const;

export const crmImportSchema = z.object({
  csv: z.string().min(1).max(2_000_000),
  /** Which list this is, e.g. "AVI-4 batch 2"; stored as the accounts' source reference. */
  batch: z.string().trim().max(200).optional(),
  /** Without it the import only reports what it would do. */
  confirm: z.boolean().default(false),
});

export interface CrmImportReport {
  confirmed: boolean; total: number; created: number;
  new: Array<{ row: number; name: string; country: string; segment: CrmSegment; priority: number | null }>;
  duplicates: Array<{ row: number; name: string; country: string; reason: 'exists' | 'repeated in file' }>;
  invalid: Array<{ row: number; name: string; errors: string[] }>;
  /** Segments named in the list that the company does not have yet; added with the accounts on confirm. */
  newSegments: Array<{ key: string; name: string }>;
}

export const crmDemoRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().toLowerCase().email().max(200),
  organisation: z.string().trim().min(1).max(200),
  /** ISO-2; unknown when the form does not ask. */
  country: z.string().trim().length(2).transform((c) => c.toUpperCase()).optional(),
  phone: text(50),
  role: text(200),
  message: text(5000),
  /** The landing page the form was on, e.g. "lp-ncc". */
  source: z.string().trim().min(1).max(100).default('website'),
});
export type CrmDemoRequestDto = z.input<typeof crmDemoRequestSchema>;

// ---- data subjects (AVI-13 §8, §8a) ----------------------------------------------------

export const CRM_ERASURE_CHANNELS = ['portal', 'email', 'phone', 'other'] as const;

export const crmEraseSchema = z.object({
  reason: z.string().trim().min(1).max(500).default('Erasure request'),
  /** How the request reached us. */
  requestedVia: z.enum(CRM_ERASURE_CHANNELS),
});

export const crmMyDataRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
});

/** Everything held about one person, as they see it on "My data". */
export interface CrmMyDataRecord {
  personId: string; name: string; roleTitle: string | null; email: string | null; phone: string | null; linkedinUrl: string | null;
  accountName: string; lawfulBasis: CrmLawfulBasis; basisNote: string | null; source: string;
  optedOutAt: string | null; erasureRequestedAt: string | null; createdAt: string;
  activities: Array<{ occurredAt: string; kind: CrmActivityKind; direction: CrmActivityDirection | null; subject: string; body: string | null }>;
  /** Every address held, with its status. */
  emails: Array<{ address: string; status: string; source: string; primary: boolean }>;
  /** Every outreach mail sent to them. */
  sends: Array<{ at: string; address: string; subject: string; status: string }>;
}

export interface CrmMyData { companyName: string; records: CrmMyDataRecord[] }

/** The reply to a public request, identical whether or not the address is known. */
export const CRM_MY_DATA_REQUEST_REPLY = 'If we hold data about this address, a link to view it has been sent there. The link works for 24 hours.';

// ---- records returned ---------------------------------------------------------------

export interface CrmSegmentInfo {
  key: CrmSegment; name: string; description: string | null;
  /** Built-in segments cannot be renamed or deleted. */
  builtIn: boolean;
  accountCount: number;
}

export interface CrmAccount {
  id: string; name: string; country: string; website: string | null; segment: CrmSegment; operatorType: CrmOperatorType | null;
  fleetSize: string | null; mainAircraft: string | null; aocReference: string | null; priority: number | null;
  triggerNote: string | null; smsApproach: string | null; source: CrmAccountSource; sourceRef: string | null; sourceUrl: string | null;
  ownerUserId: string | null; ownerName: string | null; lifecycle: CrmLifecycle; customerId: string | null; notes: string | null;
  emailDomain: string | null; emailPatterns: DomainPatternMap;
  peopleCount: number; openDealCount: number; createdAt: string; updatedAt: string;
}

export interface CrmPerson {
  id: string; accountId: string; accountName: string; name: string; firstName: string | null; lastName: string | null; initials: string | null;
  roleTitle: string | null; persona: CrmPersona;
  /** The primary address, mirrored from `emails`. */
  email: string | null;
  emails: CrmEmailAddress[]; phone: string | null; linkedinUrl: string | null; lawfulBasis: CrmLawfulBasis; basisNote: string | null;
  source: string; optedOutAt: string | null; optOutChannel: string | null; firstContactedAt: string | null; lastContactedAt: string | null;
  createdAt: string; updatedAt: string;
}

export interface CrmDeal {
  id: string; accountId: string; accountName: string; title: string; stage: DealStage; stageChangedAt: string;
  ownerUserId: string | null; ownerName: string | null; primaryPersonId: string | null; primaryPersonName: string | null;
  modules: string[]; expectedCloseDate: string | null; demoAt: string | null; proposalUrl: string | null;
  lostReason: DealLostReason | null; lostNote: string | null; wonAt: string | null; firstInvoiceId: string | null; paidAt: string | null;
  createdAt: string; updatedAt: string;
}

export interface CrmActivity {
  id: string; accountId: string; accountName: string; dealId: string | null; personId: string | null; personName: string | null;
  kind: CrmActivityKind; direction: CrmActivityDirection | null; subject: string; body: string | null; occurredAt: string;
  dueAt: string | null; doneAt: string | null; createdByUserId: string | null; createdByName: string | null; createdVia: CrmActivitySource;
}

export interface CrmStageEvent { id: string; dealId: string; fromStage: DealStage | null; toStage: DealStage; at: string; byUserId: string | null; byName: string | null }

export interface CrmAccountDetail extends CrmAccount {
  people: CrmPerson[]; deals: CrmDeal[]; activities: CrmActivity[];
}

export interface CrmDealDetail extends CrmDeal {
  stageEvents: CrmStageEvent[]; activities: CrmActivity[];
}

export interface CrmFunnelLine { key: CrmFunnelLineKey; target: number; count: number }

export interface CrmFunnel {
  from: string | null; to: string;
  /**
   * qualifiedContacts: accounts we can approach now (not do-not-contact, at least one reachable person not opted out).
   * The other lines count deals that reached at least that stage within the period; payingCustomers counts accounts.
   */
  lines: CrmFunnelLine[];
  /** Share of the previous line that reached the next one, 0–1; null when the previous line is 0. */
  conversions: Array<{ from: CrmFunnelLineKey; to: CrmFunnelLineKey; rate: number | null }>;
  /** Deals per current stage, regardless of the period. */
  pipeline: Record<DealStage, number>;
  /** Deals lost within the period, per reason. */
  lostByReason: Partial<Record<DealLostReason, number>>;
  weeklyContacts: { count: number; cap: number };
  /** All-time paying customers against the goal, regardless of the period. */
  goal: { payingCustomers: number; target: number; deadline: string };
}
